import { Injectable, Inject } from '@angular/core';
import { APP_CONFIG } from '../app-config.constants';
import { IAppConfig } from '../app-config.interface';
import { HttpClient } from '@angular/common/http';
import { ObjectResponse, SearchResult } from '../classes/object-response';
import {Agent, AgentConfig, AgentFilters} from '../classes/agent';
import { Listing } from '../classes/listing';
import { HttpParams } from '@angular/common/http';
import { Pagination } from '../classes/pagination';
import {Helper} from '../classes/helper';
import {SearchFiltersParams} from '../classes/search-filters-params';
import { CacheService } from './cache.service';
import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiConfigService } from './api-config/api-config.service';
import { PreferencesService } from './preferences.service';
import { HasSubscriptions } from '../components/has-subscriptions';
import { ListingCategory } from '../classes/enums';
import { Router } from '@angular/router';

@Injectable()
export class AgentService extends HasSubscriptions {

  private _randomSeed: string;
  // browser navigation should use the previously generated seed for the agent data 
  // when navigating after component destroy through browser back/forward buttons
  private KeyAgentRandomSeed = 'agent-search-page.randomSeed';

  private _favouriteListings: string[] = [];
  private _favouriteAgents: string[] = [];

  constructor(@Inject(APP_CONFIG) private appConfig: IAppConfig,
      private http: HttpClient,
      private cache: CacheService,
      private apiConfig: ApiConfigService,
      private preferencesService: PreferencesService,
      private _router: Router
    ) {
      super();
      this._randomSeed = '' + new Date().getTime();
      this.addSub(this.preferencesService.favouriteListingsChanged.subscribe((favourites) => this._favouriteListings = favourites));
      this.addSub(this.preferencesService.favouriteAgentsChanged.subscribe((favourites) => this._favouriteAgents = favourites));
  }


  getAgentDetails(agentId: string, isSourceSystemId?: boolean): Observable<Agent> {
    const params = new HttpParams()
      .set((isSourceSystemId ? 'sourceSystemId' : 'prefixedid'), '' + agentId);
    const base = this.getBaseUrl(this.appConfig.urls.agent.base);
    const route =  this.appConfig.urls.agent.base;
    const hashKey = this.cache.getRequestHash(route, params);
    const hashValue = this.cache.get<Agent[]>(hashKey);

    if (hashValue) {
      return of(new Agent(hashValue, this._favouriteAgents));
    }
    return this.http.get<ObjectResponse<Agent>>(base + route, { params: params, withCredentials: true })
      .pipe(
        tap(res => this.cache.set(hashKey, res.result, 300, false)),
        map(res => {
          if ('redirectUrl' in res.result && res.result['redirectUrl'].length != 0) {
            this._router.navigate([`/${res.result['redirectUrl']}`]);
            return new Agent(res.result, this._favouriteAgents);
          }
          else if ('active' in res.result && !res.result['active']) {
            // it will redirect to agent roaster url
            throw new Error('You are trying to access an inactive agent');
          }
          else {
            return new Agent(res.result, this._favouriteAgents)
          }
        })
      );
  }

  async getFilters(propType): Promise<AgentFilters> {
    const params = new HttpParams()
    .set('category', propType === ListingCategory.COMMERCIAL ? 'Commercial' : 'Residential');
    return await this.http.get<ObjectResponse<AgentFilters>>(this.getBaseUrl(this.appConfig.urls.agent.filters) + this.appConfig.urls.agent.filters, {params, withCredentials: true})
      .pipe(map(res => {
        const filters = res.result as AgentFilters;
        filters.specialties.sort((o1, o2) => o1 > o2 ? 1 : -1);
        filters.languages.sort((o1, o2) => o1 > o2 ? 1 : -1);
        return filters;
      }))
      .toPromise();
  }

  /**
   * Returns a paginated list of listings for an agent
   * @param remaxAgentId
   * @param pagination
   */
  async getListingsPreviewsForAgent(remaxAgentId: string, pagination: Pagination) {
    const params = this._setSearchParams(pagination);
    const base = this.getBaseUrl(this.appConfig.urls.agent.listings);
    const route = this.appConfig.urls.agent.listings + remaxAgentId;
    const hashKey = this.cache.getRequestHash(route, params);
    const hashValue = this.cache.get<SearchResult<Listing[]>>(hashKey);

    // FIXME: Favourites are not loading properly
    if (hashValue) {
      hashValue.results = hashValue.results.map(listing => new Listing(listing, this._favouriteListings));
      return new Promise<SearchResult<Listing[]>>((resolve) => {
        resolve(hashValue);
      });
    }

    const result = await this.http.get<ObjectResponse<SearchResult<Listing[]>>>(base + route, {params: params, withCredentials: true })
      .pipe(map(res => {
        res.result.results = res.result.results.map((r) => new Listing(r, this._favouriteListings));
        this.cache.set(hashKey, res.result, 300);
        return res.result;
      }))
      .toPromise();
    return result;
  }

  /**
   * Searches across all Agents
   * @param pagination
   * @param searchParams
   */
  async getAgents(pagination: Pagination, searchParams: SearchFiltersParams, listingCategory, shouldUseCachedSeed: boolean = false) {
    if (!searchParams.randomSeed) {
      const seed = shouldUseCachedSeed ? this.cache.get<string>(this.KeyAgentRandomSeed, true) : this._randomSeed;
      searchParams.randomSeed = seed;
    }

    this.cache.set(this.KeyAgentRandomSeed, searchParams.randomSeed, 300, true);

    const params = Helper.setSearchParams(pagination, searchParams, listingCategory);

    const result = await this.http.get<ObjectResponse<SearchResult<Agent[]>>>(this.getBaseUrl(this.appConfig.urls.agent.search) + this.appConfig.urls.agent.search, { params: params, withCredentials: true})
      .pipe(map(res => {
        res.result.results = res.result.results ? res.result.results.map((o) => new Agent(o)) : [];
        return res.result;
      }))
      .toPromise();
    return result;
  }

  /**
   * Returns the total number of agents for a specific, province, city or for the whole country
   * If province and city and not passed in, will search for all of canada
   * @param province
   * @param city
   */
  async getAgentCount(province?: string, city?: string): Promise<number> {
    const base = this.getBaseUrl(this.appConfig.urls.agent.count);
    let route = this.appConfig.urls.agent.count;
    if (province) route += province;
    if (city) route += '/' + city;

    const hashKey = this.cache.getRequestHash(route);
    const hashValue = this.cache.get<number>(hashKey, true);

    if (hashValue) {
      return new Promise<number>((resolve) => {
        resolve(hashValue);
      });
    }

    const url = base + route;

    const result = await this.http.get<ObjectResponse<number>>(url, { withCredentials: true})
      .pipe(
        tap(res => this.cache.set(hashKey, res.result, 300, true)),
        map(res => res.result)
      )
      .toPromise();
    return result;
  }

  private _setSearchParams(pagination?: Pagination): HttpParams {
    let params = new HttpParams();
    if (pagination) {
      params = params.append('from', (pagination.offset).toString());
      params = params.append('size', pagination.limit.toString());
    }
    return params;

  }

  private getBaseUrl(route: string): string {
    return this.apiConfig.getServerRoot(route);
  }

  async getAgentConfig(): Promise<AgentConfig> {
    const base = this.getBaseUrl(this.appConfig.urls.agent.config);
    const route = this.appConfig.urls.agent.config;

    const hashKey = this.cache.getRequestHash(route);
    const hashValue = this.cache.get<AgentConfig>(hashKey);

    if (hashValue) {
      return new Promise<AgentConfig>((resolve) => {
        resolve(hashValue);
      });
    }

    const url = base + route;

    const result = await this.http.get<ObjectResponse<AgentConfig>>(url, { withCredentials: true})
      .pipe(
        tap(res => this.cache.set(hashKey, res.result, 3600)),
        map(res => res.result)
      )
      .toPromise();
    return result;
  }
}
